import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';

import { Icon } from 'js/components';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import DropIndicator from 'js/components/Assessment/Editors/common/components/DropIndicator';
import { useRubricContext } from './RubricContext';
import { isNullOrWhitespace } from 'js/utils/string';
import { formatRetiredAtLabel } from 'js/utils/rubric';
import RubricMeasurementActions from './RubricMeasurementActions';
import styles from './RubricEditor.module.scss';

function SourceDragPreview({ origin, text }) {
  return (
    <div className={classNames(styles['source-drag-preview'], 'drag-preview')}>
      <span className={styles['drag-handle']}>
        <Icon icon={faGripVertical} className="icon" />
      </span>
      <span className={styles['source-origin']}>{origin}</span>
      <span className={classNames(styles['source-text'], 'line-clamp-2')}>
        {text}
      </span>
    </div>
  );
}

function RubricQuestionSource({
  source,
  questionSetGuid,
  questionGuid,
  sourceIndex,
  sourceCount = 0,
  tag: Tag = 'li',
}) {
  const rootRef = useRef(null);
  const dragHandleRef = useRef(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const [dragging, setDragging] = useState(false);

  const {
    moveRubricSourceBefore,
    moveRubricSourceAfter,
    removeRubricSource,
    setRubricSourceRetiredAt,
  } = useRubricContext();

  const { assessment_key, key, assessment_name, question_text } = source;

  const text = question_text ?? key;
  const origin = assessment_name ?? assessment_key;

  const sourceGuid = source.guid;

  useEffect(() => {
    const el = rootRef.current;
    const dragHandle = dragHandleRef.current;
    if (!el || !dragHandle || !sourceGuid) {
      return undefined;
    }

    return combine(
      draggable({
        element: el,
        dragHandle,
        getInitialData: () => ({
          rubricSource: source,
          questionSetGuid,
          questionGuid,
          sourceIndex,
        }),
        onDragStart: () => setDragging(true),
        onDrop: () => setDragging(false),
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            render({ container }) {
              ReactDOM.render(
                <SourceDragPreview origin={origin} text={text} />,
                container
              );
              return function cleanup() {
                ReactDOM.unmountComponentAtNode(container);
              };
            },
            getOffset: () => ({ x: 16, y: 12 }),
            nativeSetDragImage,
          });
        },
      }),
      dropTargetForElements({
        element: el,
        canDrop: ({ source: dragSource }) =>
          !!(
            dragSource.data.rubricSource &&
            dragSource.data.questionGuid === questionGuid &&
            dragSource.data.rubricSource.guid !== sourceGuid
          ),
        getData: (args) =>
          attachClosestEdge(
            {
              rubricSource: source,
              questionSetGuid,
              questionGuid,
              sourceIndex,
            },
            {
              input: args.input,
              element: args.element,
              allowedEdges: ['top', 'bottom'],
            }
          ),
        onDragEnter: () => setIsDraggedOver(true),
        onDragLeave: () => {
          setClosestEdge(null);
          setIsDraggedOver(false);
        },
        onDrag({ self, source: dragSource }) {
          const isSource = dragSource.element === el;
          if (isSource || !dragSource.data.rubricSource) {
            setClosestEdge(null);
            return;
          }

          if (dragSource.data.questionGuid !== questionGuid) {
            setClosestEdge(null);
            return;
          }

          const srcIdx = dragSource.data.sourceIndex;
          const isItemBeforeSource = sourceIndex === srcIdx - 1;
          const isItemAfterSource = sourceIndex === srcIdx + 1;
          const edge = extractClosestEdge(self.data);
          const isDropIndicatorHidden =
            (isItemBeforeSource && edge === 'bottom') ||
            (isItemAfterSource && edge === 'top');
          if (isDropIndicatorHidden) {
            setClosestEdge(null);
            return;
          }

          setClosestEdge(edge);
        },
        onDrop: ({ source: dragSource, location }) => {
          setIsDraggedOver(false);
          const dropTarget = location.current.dropTargets[0];
          const edge = extractClosestEdge(dropTarget.data);
          setClosestEdge(null);

          const rs = dragSource.data.rubricSource;
          if (!rs || dragSource.data.questionGuid !== questionGuid || !edge) {
            return;
          }

          if (edge === 'bottom') {
            moveRubricSourceAfter(
              questionSetGuid,
              questionGuid,
              rs.guid,
              sourceGuid
            );
          } else {
            moveRubricSourceBefore(
              questionSetGuid,
              questionGuid,
              rs.guid,
              sourceGuid
            );
          }
        },
        getIsSticky: (args) => {
          const elementUnderCursor = document.elementFromPoint(
            args.input.pageX,
            args.input.pageY
          );
          return (
            elementUnderCursor &&
            elementUnderCursor.closest('.rubric-source-droppable-area')
          );
        },
      })
    );
  }, [
    source,
    sourceGuid,
    questionSetGuid,
    questionGuid,
    sourceIndex,
    origin,
    text,
    moveRubricSourceBefore,
    moveRubricSourceAfter,
  ]);

  const slotClassName = useMemo(
    () =>
      classNames(styles['source-slot'], {
        [styles['source-slot-dragging']]: dragging,
      }),
    [dragging]
  );

  const sourceQuestionText = question_text ?? key;

  const sourceMeasurementProps = {
    trigger: 'dropdown',
    ariaLabel: 'Source actions',
    modalTitle: 'Remove source?',
    detailPrimary: origin,
    detailSecondary: sourceQuestionText,
    retireDescription:
      'Stop counting this source toward scores after the retirement date.',
    dropdownMenuLabel: 'Remove…',
    retiredAt: source.retired_at,
    onRetire: (date) =>
      setRubricSourceRetiredAt(questionSetGuid, questionGuid, sourceGuid, date),
    onClear: () =>
      setRubricSourceRetiredAt(questionSetGuid, questionGuid, sourceGuid, null),
    onDelete: () =>
      removeRubricSource(questionSetGuid, questionGuid, sourceGuid),
    ...(sourceCount <= 1
      ? {
          deleteNote:
            'The question will have no sources until you add another.',
        }
      : {}),
  };

  return (
    <Tag className={slotClassName} ref={rootRef}>
      <div
        className={classNames(styles['source-inner'], {
          [styles['source-inner--single']]: sourceCount <= 1,
        })}
      >
        <button
          type="button"
          className="drag-handle"
          ref={dragHandleRef}
          aria-label="Drag to reorder source"
        >
          <Icon icon={faGripVertical} className="icon" />
        </button>
        <span className={styles['source-counter']}></span>
        <span className={styles['source-origin']}>
          <span>{origin}</span>
          {(sourceCount > 1 || !!source.retired_at) && (
            <RubricMeasurementActions {...sourceMeasurementProps} />
          )}
        </span>
        <span className={styles['source-text']}>
          {!isNullOrWhitespace(text) ? text : '—'}
          {source.retired_at && formatRetiredAtLabel(source.retired_at) && (
            <div className={styles['retired-badge']}>
              Retired {formatRetiredAtLabel(source.retired_at)}
            </div>
          )}
        </span>
      </div>
      {isDraggedOver && closestEdge !== null && (
        <DropIndicator
          top={closestEdge === 'top'}
          bottom={closestEdge === 'bottom'}
        />
      )}
    </Tag>
  );
}

export default RubricQuestionSource;
